'use client';
import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useAnalytics } from '..';
import { useClientOnlyConstant } from '../../hooks/useClientOnlyConstant';
import { Shibuya } from '../../Shibuya';

export interface AnalyticsPageViewContextValue {
  pageName: string;
  pageViewId: string;
  pageViewReady: boolean;
  contentId?: Shibuya.PagePayload['content_id'];
  contentSource?: Shibuya.PagePayload['content_source'];
  pageType?: string;
}

const AnalyticsPageViewContext = createContext<AnalyticsPageViewContextValue | null>(null);

export interface PageViewProps {
  name?: string;
  contentId?: Shibuya.PagePayload['content_id'];
  contentSource?: Shibuya.PagePayload['content_source'];
  dangerouslyIgnoreCanonicalUrl?: boolean;
  pageType?: string;
}

export const AnalyticsPageView = ({
  name = '',
  contentId,
  contentSource,
  dangerouslyIgnoreCanonicalUrl,
  children,
  pageType = '',
}: PropsWithChildren<PageViewProps>) => {
  const { analytics, deployableName, deployableVersion, deviceId, gitSha } = useAnalytics();
  const [pageViewReady, setPageViewReady] = useState(false);
  const pageViewId = useClientOnlyConstant(() => uuid(), '');
  useEffect(() => {
    if (!analytics) return;
    if (deviceId) {
      const payload: Shibuya.PagePayload = {
        deployable_name: deployableName,
        deployable_version: deployableVersion,
        pageview_id: pageViewId,
        traditional_device_id: deviceId,
        content_id: contentId,
        content_source: contentSource,
        page_type: pageType,
      };
      if (dangerouslyIgnoreCanonicalUrl) {
        payload.path = window.location.pathname;
        payload.url = window.location.href;
      }

      if (name) {
        analytics.page(name, payload, {
          context: {
            app: {
              name: deployableName,
              version: gitSha,
            },
          },
        });
      } else {
        analytics.page(payload, undefined, {
          context: {
            app: {
              name: deployableName,
              version: gitSha,
            },
          },
        });
      }
      setPageViewReady(true);
    }
  }, [deviceId]);

  return (
    <AnalyticsPageViewContext.Provider
      value={{
        pageName: name,
        pageViewId: pageViewId,
        pageViewReady,
        contentId,
        contentSource,
        pageType,
      }}
    >
      {children}
    </AnalyticsPageViewContext.Provider>
  );
};

export const useAnalyticsPageView = () => {
  const context = useContext(AnalyticsPageViewContext);
  if (!context) {
    throw new Error('useAnalyticsPageView must be used within a AnalyticsPageView');
  }
  return context;
};
